import { formatUuidT } from "../../flatbuffers/display";
import type { UuidT } from "../../flatbuffers/generated/front-end/uuid";

/**
 * A catalogue row as it arrives: a FlatBuffer-decoded object (`OffsetTypeT`,
 * `VendorT`, `DataBlockTypeT`, with `UuidT` ids and an offset type's
 * attributes in `attributesJson`), or the JSON object the API sent before.
 */
type RawRow = Record<string, unknown>;

/** A catalogue row as the screens read it: string ids and plain fields. */
export type CatalogRow = Record<string, unknown> & { id: string };

const idOf = (value: unknown): string =>
  formatUuidT(value as UuidT | string | number | null | undefined);

const idsOf = (value: unknown): unknown => (Array.isArray(value) ? value.map(idOf) : value);

/**
 * An offset type's attribute switches as an object. The FlatBuffer API sends
 * them as a JSON string (`attributesJson`); a JSON reply already had the
 * object. A string that does not parse to an object reads as no attributes.
 */
function attributesOf(row: RawRow): Record<string, unknown> {
  if (typeof row.attributes === "object" && row.attributes !== null) {
    return row.attributes as Record<string, unknown>;
  }
  if (typeof row.attributesJson !== "string" || row.attributesJson === "") {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(row.attributesJson);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/** A vendor or data block type row: its id as a string, every other field as sent. */
export function catalogRow(row: RawRow): CatalogRow {
  return { ...row, id: idOf(row.id) };
}

/**
 * An offset type row: string ids (its own, and every data block and offset
 * id list) and its attribute switches as the `attributes` object the table,
 * drawer and form read.
 */
export function offsetTypeRow(row: RawRow): CatalogRow {
  return {
    ...row,
    id: idOf(row.id),
    attributes: attributesOf(row),
    dataBlockIds: idsOf(row.dataBlockIds),
    dataBlockIds2delete: idsOf(row.dataBlockIds2delete),
    offsetIds: idsOf(row.offsetIds),
    offsetIds2delete: idsOf(row.offsetIds2delete),
  };
}

/**
 * A paginated list reply with every row passed through `toRow`. Anything that
 * is not a list reply (an error result, a body without `data`) is returned as
 * it came.
 */
export function withCatalogRows<Reply>(reply: Reply, toRow: (row: RawRow) => CatalogRow): Reply {
  if (typeof reply !== "object" || reply === null) {
    return reply;
  }
  const { data } = reply as { data?: unknown };
  if (!Array.isArray(data)) {
    return reply;
  }
  return { ...reply, data: data.map((row) => toRow(row as RawRow)) };
}
